import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_industrial-clean-pro/artifacts/fdqrs7d1_Untitled%20design%20%289%29.png";
const LOGO_DARK_URL = "https://customer-assets.emergentagent.com/job_industrial-clean-pro/artifacts/5o9nd5tr_PKC%20Property%20Services%20Pty%20Ltd_E2.jpeg";

const NAV_LINKS = [
  { label: "Home", path: "/" },
  { label: "About", path: "/about" },
  { label: "Services", path: "/services" },
  { label: "Our Work", path: "/ourwork" },
  { label: "Team", path: "/team" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === "/";

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []); // Only run on mount

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]); // Use location.pathname instead of location object

  // Only show solid nav when scrolled, transparent at top on all pages
  // Also show solid nav when mobile menu is open
  const showSolidNav = scrolled || mobileOpen;

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        showSolidNav ? "bg-white shadow-sm" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-24 md:h-28">
          <Link to="/" className="flex items-center" data-testid="nav-logo">
            <img
              src={LOGO_URL}
              alt="PKC Property Services"
              className="h-[140px] md:h-[180px] w-auto"
              style={showSolidNav ? { filter: 'invert(1)' } : {}}
            />
          </Link>

          <div className="hidden lg:flex items-center gap-8">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-link-${link.label.toLowerCase()}`}
                className={`text-[13px] font-medium tracking-wide transition-colors duration-300 ${
                  location.pathname === link.path
                    ? "text-[#B8D86B]"
                    : showSolidNav 
                      ? "text-neutral-600 hover:text-neutral-900" 
                      : "text-white/90 hover:text-white"
                }`}
                style={{ fontFamily: 'Manrope, sans-serif' }}
              >
                {link.label}
              </Link>
            ))}
            <Link
              to="/contact"
              data-testid="nav-book-consult-btn"
              className="bg-[#B8D86B] text-[#1A2E05] px-6 py-2.5 text-[13px] font-semibold tracking-wide hover:bg-[#A3C65A] transition-colors duration-300"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              Contact
            </Link>
          </div>

          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden p-2"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            {mobileOpen ? (
              <X className={`w-5 h-5 ${showSolidNav ? 'text-neutral-900' : 'text-white'}`} />
            ) : (
              <Menu className={`w-5 h-5 ${showSolidNav ? 'text-neutral-900' : 'text-white'}`} />
            )}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-white border-t border-neutral-100"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-6 space-y-4">
              {NAV_LINKS.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`block text-base font-medium ${
                    location.pathname === link.path ? "text-[#B8D86B]" : "text-neutral-700"
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <Link
                to="/contact"
                className="block bg-[#B8D86B] text-[#1A2E05] px-6 py-3 text-sm font-semibold text-center"
              >
                Contact
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};
